import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router";
import api from "../../api/client";

function unwrapApiArray(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

function normalizeDbMenuItem(row) {
  return {
    id: row.id,
    name: row.name ?? "",
    price: row.price ?? "",
    description: row.description ?? "",
    image: row.image ?? null,
    enabled: row.enabled == null ? 1 : Number(row.enabled) ? 1 : 0,
    menuId: row.menuId ?? null,
    menuName: row.menuName ?? "",
    categoryId: row.categoryId ?? null,
    categoryName: row.categoryName ?? "",
  };
}

function makeEmptyForm(defaultMenuId, defaultCategoryId) {
  return {
    id: null,
    name: "",
    menuId: defaultMenuId ?? "",
    categoryId: defaultCategoryId ?? "",
    price: "",
    description: "",
    image: "",
    enabled: true,
  };
}

export const AdminMenuEdit = () => {
  const [menus, setMenus] = useState([]);
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [menuFilter, setMenuFilter] = useState("All");
  const [catFilter, setCatFilter] = useState("All");
  const [showDisabled, setShowDisabled] = useState(true);

  // Overflow menu — tracks which row's "⋯" is open
  const [openRowMenu, setOpenRowMenu] = useState(null);
  const closeRowMenu = useCallback(() => setOpenRowMenu(null), []);
  useEffect(() => {
    if (openRowMenu == null) return;
    document.addEventListener("click", closeRowMenu);
    return () => document.removeEventListener("click", closeRowMenu);
  }, [openRowMenu, closeRowMenu]);

  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create"); // "create" | "edit"
  const [activeItemId, setActiveItemId] = useState(null);
  const [form, setForm] = useState(() => makeEmptyForm("", ""));
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const imageInputRef = useRef(null);

  // Menus/categories editor modals
  const [menusModalOpen, setMenusModalOpen] = useState(false);
  const [menuEdits, setMenuEdits] = useState([]);

  const [catsModalOpen, setCatsModalOpen] = useState(false);
  const [catMenuFilter, setCatMenuFilter] = useState("");
  const [catEdits, setCatEdits] = useState([]);

  // ---- Data fetching ----

  async function fetchMenus() {
    const res = await api.get("/menu/menus");
    const list = unwrapApiArray(res.data);
    setMenus(list);
    return list;
  }

  async function fetchCategories() {
    const res = await api.get("/menu/categories");
    const list = unwrapApiArray(res.data);
    setCategories(list);
    return list;
  }

  async function fetchMenuAdmin() {
    const res = await api.get("/menu/admin");
    const list = unwrapApiArray(res.data).map(normalizeDbMenuItem);
    setMenuItems(list);
    return list;
  }

  useEffect(() => {
    const init = async () => {
      try {
        setLoading(true);
        setError("");
        await Promise.all([fetchMenus(), fetchCategories(), fetchMenuAdmin()]);
      } catch (e) {
        console.error(e);
        setError("Failed to load menu/categories");
      } finally {
        setLoading(false);
      }
    };
    init();
  }, []);

  // ---- Derived data ----

  const formCategories = useMemo(() => {
    if (form.menuId === "" || form.menuId == null) return categories;
    return categories.filter((c) => Number(c.menuId) === Number(form.menuId));
  }, [categories, form.menuId]);

  const filterCategories = useMemo(() => {
    if (menuFilter === "All") return categories;
    return categories.filter((c) => Number(c.menuId) === Number(menuFilter));
  }, [categories, menuFilter]);

  const filteredMenu = useMemo(() => {
    const q = query.trim().toLowerCase();
    return menuItems
      .filter((m) => {
        if (!showDisabled && !m.enabled) return false;
        if (menuFilter !== "All") {
          const wanted = Number(menuFilter);
          if (Number.isFinite(wanted) && Number(m.menuId) !== wanted) return false;
        }
        if (catFilter !== "All" && catFilter !== "Other") {
          const wanted = Number(catFilter);
          if (Number.isFinite(wanted) && Number(m.categoryId) !== wanted) return false;
        }
        if (catFilter === "Other" && m.categoryId !== null) return false;
        if (!q) return true;
        return [m.name, m.description, m.categoryName, m.menuName]
          .some((f) => String(f || "").toLowerCase().includes(q));
      })
      .sort((a, b) => {
        if (a.enabled !== b.enabled) return a.enabled ? -1 : 1;
        return (
          String(a.menuName || "").localeCompare(String(b.menuName || "")) ||
          String(a.categoryName || "").localeCompare(String(b.categoryName || "")) ||
          String(a.name || "").localeCompare(String(b.name || ""))
        );
      });
  }, [menuItems, query, menuFilter, catFilter, showDisabled]);

  // ---- Modal helpers ----

  function openAdd() {
    const defaultMenuId = menus?.[0]?.id ?? "";
    const defaultCatId = categories?.[0]?.id ?? "";
    setModalMode("create");
    setActiveItemId(null);
    setForm(makeEmptyForm(defaultMenuId, defaultCatId));
    setImageFile(null);
    setImagePreview(null);
    setModalOpen(true);
  }

  function openEdit(item) {
    setModalMode("edit");
    setActiveItemId(item.id);
    setForm({
      id: item.id,
      name: item.name ?? "",
      menuId: item.menuId ?? "",
      categoryId: item.categoryId ?? "",
      price: item.price ?? "",
      description: item.description ?? "",
      image: item.image ?? "",
      enabled: !!item.enabled,
    });
    setImageFile(null);
    setImagePreview(item.image ? `/image_uploads/${item.image}` : null);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
  }

  // ---- CRUD ----

  async function submitForm(e) {
    e.preventDefault();

    const name = String(form.name || "").trim();
    if (!name) return alert("Please enter a menu item name.");
    if (form.menuId === "" || form.menuId == null) return alert("Please select a menu.");

    let imageFilename = form.image ? String(form.image) : null;

    if (imageFile) {
      try {
        const uploadRes = await api.upload("/images", imageFile);
        imageFilename = uploadRes?.data?.filename ?? null;
      } catch (err) {
        console.error(err);
        alert("Failed to upload image.");
        return;
      }
    }

    const payload = {
      name,
      menuId: Number(form.menuId),
      categoryId: form.categoryId ? Number(form.categoryId) : null,
      price: form.price,
      description: String(form.description || "").trim(),
      image: imageFilename,
      enabled: form.enabled ? 1 : 0,
    };

    try {
      setSaving(true);
      setError("");
      if (modalMode === "create") {
        await api.post("/menu", payload);
      } else {
        if (!activeItemId) return alert("Missing active menu id");
        await api.put(`/menu/${activeItemId}`, payload);
      }
      closeModal();
      await fetchMenuAdmin();
    } catch (err) {
      console.error(err);
      setError("Failed to save menu item");
      alert("Failed to save menu item. Check server logs.");
    } finally {
      setSaving(false);
    }
  }

  async function toggleEnabled(item) {
    if (!item?.id) return;
    const willEnable = !item.enabled;

    try {
      setSaving(true);
      await api.patch(`/menu/${item.id}`, { enabled: willEnable ? 1 : 0 });
      await fetchMenuAdmin();
    } catch (err) {
      console.error(err);
      alert(`Failed to ${willEnable ? "enable" : "disable"} menu item.`);
    } finally {
      setSaving(false);
    }
  }

  async function duplicateItem(item) {
    try {
      setSaving(true);
      await api.post("/menu", {
        name: `${item.name} (Copy)`,
        menuId: Number(item.menuId),
        categoryId: item.categoryId ? Number(item.categoryId) : null,
        price: item.price ?? "",
        description: item.description ?? "",
        image: item.image ?? null,
        enabled: item.enabled ? 1 : 0,
      });
      await fetchMenuAdmin();
    } catch (err) {
      console.error(err);
      alert("Failed to duplicate menu item.");
    } finally {
      setSaving(false);
    }
  }

  async function deleteItem(item) {
    if (!item?.id) return;
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;

    try {
      setSaving(true);
      await api.delete(`/menu/${item.id}`);
      await fetchMenuAdmin();
    } catch (err) {
      console.error(err);
      alert("Failed to delete menu item.");
    } finally {
      setSaving(false);
    }
  }

  // ---- Menu / Category editing ----

  function openMenusModal() {
    setMenuEdits(
      menus
        .filter((m) => m.id !== 0)
        .map((m) => ({ id: m.id, name: m.name, originalName: m.name, isNew: false, deleted: false }))
    );
    setMenusModalOpen(true);
  }

  function openCatsModal() {
    const mId = menus[0]?.id != null ? String(menus[0].id) : "";
    setCatMenuFilter(mId);
    setCatEdits(
      categories
        .filter((c) => Number(c.menuId) === Number(mId))
        .map((c) => ({ id: c.id, name: c.name, originalName: c.name, isNew: false, deleted: false }))
    );
    setCatsModalOpen(true);
  }

  function updateCatEditsForMenu(mId) {
    setCatMenuFilter(mId);
    setCatEdits(
      categories
        .filter((c) => Number(c.menuId) === Number(mId))
        .map((c) => ({ id: c.id, name: c.name, originalName: c.name, isNew: false, deleted: false }))
    );
  }

  async function saveMenus() {
    const adds = menuEdits.filter((e) => e.isNew && !e.deleted && e.name.trim()).map((e) => e.name.trim());
    const renames = menuEdits.filter((e) => !e.isNew && !e.deleted && e.name.trim() !== e.originalName).map((e) => ({ id: e.id, name: e.name.trim() }));
    const deletes = menuEdits.filter((e) => !e.isNew && e.deleted).map((e) => e.id);

    if (!adds.length && !renames.length && !deletes.length) { setMenusModalOpen(false); return; }

    try {
      setSaving(true);
      const result = await api.post("/menu/menus/batch", { adds, renames, deletes });
      if (result?.warning) alert(result.warning);
      await Promise.all([fetchMenus(), fetchCategories(), fetchMenuAdmin()]);
      setMenusModalOpen(false);
    } catch (err) {
      console.error(err);
      alert("Failed to save menus.");
    } finally {
      setSaving(false);
    }
  }

  async function saveCategories() {
    const adds = catEdits.filter((e) => e.isNew && !e.deleted && e.name.trim()).map((e) => e.name.trim());
    const renames = catEdits.filter((e) => !e.isNew && !e.deleted && e.name.trim() !== e.originalName).map((e) => ({ id: e.id, name: e.name.trim() }));
    const deletes = catEdits.filter((e) => !e.isNew && e.deleted).map((e) => e.id);

    if (!adds.length && !renames.length && !deletes.length) { setCatsModalOpen(false); return; }

    try {
      setSaving(true);
      const result = await api.post("/menu/categories/batch", { menuId: Number(catMenuFilter), adds, renames, deletes });
      if (result?.warning) alert(result.warning);
      await Promise.all([fetchCategories(), fetchMenuAdmin()]);
      setCatsModalOpen(false);
    } catch (err) {
      console.error(err);
      alert("Failed to save categories.");
    } finally {
      setSaving(false);
    }
  }

  // ---- Render ----

  return (
    <div className="am-wrap">
      <header className="am-header">
        <div>
          <Link to="/admin" className="am-admin-back">← Admin</Link>
          <h1 className="am-title">Menu</h1>
          <p className="am-subtitle">Menu items are database-backed.</p>
          {loading && <p className="am-subtitle">Loading…</p>}
          {saving && <p className="am-subtitle">Saving…</p>}
          {error && <p className="am-subtitle" style={{ color: "crimson" }}>{error}</p>}
        </div>
        <div className="am-headerActions">
          <button className="am-btnPrimary" type="button" onClick={openAdd}>
            + Add item
          </button>
          <button className="am-btn" type="button" onClick={openMenusModal}>
            Edit menus
          </button>
          <button className="am-btn" type="button" onClick={openCatsModal}>
            Edit categories
          </button>
        </div>
      </header>

      {/* Filters */}
      <section className="am-toolbar">
        <div className="am-filters">
          <div className="am-filterGroup">
            <label className="am-label" htmlFor="am-menu">Menu</label>
            <select
              id="am-menu"
              className="am-select"
              value={menuFilter}
              onChange={(e) => { setMenuFilter(e.target.value); setCatFilter("All"); }}
            >
              <option value="All">All</option>
              {menus.map((m) => (
                <option key={m.id} value={String(m.id)}>{m.name}</option>
              ))}
            </select>
          </div>

          <div className="am-filterGroup">
            <label className="am-label" htmlFor="am-cat">Category</label>
            <select
              id="am-cat"
              className="am-select"
              value={catFilter}
              onChange={(e) => setCatFilter(e.target.value)}
            >
              <option value="All">All</option>
              <option value="Other">Other</option>
              {filterCategories.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </div>

          <div className="am-filterGroup">
            <label className="am-label" htmlFor="am-search">Search</label>
            <input
              id="am-search"
              className="am-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Name, description, category…"
            />
          </div>

          <label className="am-check">
            <input
              type="checkbox"
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
            />
            Show disabled
          </label>
        </div>
      </section>

      {/* Item list — now full width (form moved to modal) */}
      <section className="am-card">
        <div className="am-cardHeader">
          <h2 className="am-cardTitle">Menu items</h2>
          <p className="am-cardNote">{filteredMenu.length} in view</p>
        </div>

        <div className="am-list">
          {filteredMenu.length === 0 ? (
            <div className="am-emptyBig">No menu items match your filters.</div>
          ) : (
            filteredMenu.map((m) => (
              <div key={m.id} className={`am-row ${m.enabled ? "" : "is-soldout"}`}>
                <div className="am-rowMain">
                  <div className="am-rowTop">
                    <div className="am-rowTitle">
                      {m.name}
                      {!m.enabled && <span className="am-pill am-pillSold">Disabled</span>}
                    </div>
                    <div className="am-rowPrice">{m.price}</div>
                  </div>
                  <div className="am-rowMeta">
                    <span className="am-muted">{m.menuName || "No menu"}</span>
                    <span className="am-dot">›</span>
                    <span className="am-muted">{m.categoryName || "Other"}</span>
                    {m.description && (
                      <span className="am-muted am-fullFlexRow">{m.description}</span>
                    )}
                  </div>
                </div>

                <div className="am-rowActions">
                  <button
                    className="am-overflowBtn"
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setOpenRowMenu(openRowMenu === m.id ? null : m.id); }}
                    aria-label="Actions"
                  >
                    ⋯
                  </button>
                  {openRowMenu === m.id && (
                    <div className="am-dropdown">
                      <button type="button" onClick={() => { openEdit(m); setOpenRowMenu(null); }}>
                        Edit
                      </button>
                      <button type="button" onClick={() => { duplicateItem(m); setOpenRowMenu(null); }}>
                        Duplicate
                      </button>
                      <button
                        type="button"
                        onClick={() => { toggleEnabled(m); setOpenRowMenu(null); }}
                        disabled={saving}
                      >
                        {m.enabled ? "Disable" : "Enable"}
                      </button>
                      <button
                        type="button"
                        className="am-dropdownDanger"
                        onClick={() => { deleteItem(m); setOpenRowMenu(null); }}
                        disabled={saving}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </section>

      {/* ---- Modal ---- */}
      {modalOpen && (
        <div className="am-modal-backdrop" onClick={closeModal}>
          <div className="am-modal" onClick={(e) => e.stopPropagation()}>
            <div className="am-modal-header">
              <h2 className="am-cardTitle">
                {modalMode === "create" ? "Add Menu Item" : "Edit Menu Item"}
              </h2>
              <button className="am-modal-close" type="button" onClick={closeModal}>
                ×
              </button>
            </div>

            <form className="am-form" onSubmit={submitForm}>
              <div className="am-field">
                <label className="am-label" htmlFor="m-name">Name</label>
                <input
                  id="m-name"
                  className="am-input"
                  value={form.name}
                  onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                  placeholder="e.g., Birria Tacos"
                  autoFocus
                />
              </div>

              <div className="am-formRow">
                <div className="am-field">
                  <label className="am-label" htmlFor="m-menu">Menu</label>
                  <select
                    id="m-menu"
                    className="am-select"
                    value={String(form.menuId)}
                    onChange={(e) => setForm((p) => ({ ...p, menuId: e.target.value, categoryId: "" }))}
                  >
                    {menus.map((m) => (
                      <option key={m.id} value={String(m.id)}>{m.name}</option>
                    ))}
                  </select>
                </div>

                <div className="am-field">
                  <label className="am-label" htmlFor="m-cat2">Category</label>
                  <select
                    id="m-cat2"
                    className="am-select"
                    value={String(form.categoryId)}
                    onChange={(e) => setForm((p) => ({ ...p, categoryId: e.target.value }))}
                  >
                    <option value="">Other</option>
                    {formCategories.map((c) => (
                      <option key={c.id} value={String(c.id)}>{c.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="am-field">
                <label className="am-label" htmlFor="m-price">Price</label>
                <input
                  id="m-price"
                  className="am-input"
                  value={form.price}
                  onChange={(e) => setForm((p) => ({ ...p, price: e.target.value }))}
                  placeholder="e.g., 12.50"
                />
              </div>

              <div className="am-field">
                <label className="am-label" htmlFor="m-desc">Description</label>
                <textarea
                  id="m-desc"
                  className="am-textarea"
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  placeholder="Short description"
                />
              </div>

              <div className="am-field">
                <label className="am-label">Image (optional)</label>
                {imagePreview && (
                  <div className="am-image-preview">
                    <img src={imagePreview} alt="Preview" />
                    <button
                      type="button"
                      className="am-image-remove"
                      onClick={() => { setImageFile(null); setImagePreview(null); setForm((p) => ({ ...p, image: "" })); if (imageInputRef.current) imageInputRef.current.value = ""; }}
                    >
                      &times;
                    </button>
                  </div>
                )}
                <input
                  ref={imageInputRef}
                  type="file"
                  accept="image/*"
                  className="am-input"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setImageFile(file);
                    setImagePreview(URL.createObjectURL(file));
                    setForm((p) => ({ ...p, image: "" }));
                  }}
                />
              </div>

              <label className="am-check">
                <input
                  type="checkbox"
                  checked={!!form.enabled}
                  onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))}
                />
                Enabled (visible on public menu)
              </label>

              <div className="am-modal-footer">
                <button className="am-btn" type="button" onClick={closeModal} disabled={saving}>
                  Cancel
                </button>
                <button className="am-btnPrimary" type="submit" disabled={saving}>
                  {modalMode === "create" ? "Add Item" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* ---- Menus Modal ---- */}
      {menusModalOpen && (
        <div className="am-modal-backdrop" onClick={() => setMenusModalOpen(false)}>
          <div className="am-modal" onClick={(e) => e.stopPropagation()}>
            <div className="am-modal-header">
              <h2 className="am-cardTitle">Edit Menus</h2>
              <button className="am-modal-close" type="button" onClick={() => setMenusModalOpen(false)}>
                &times;
              </button>
            </div>

            <table className="am-entity-table">
              <tbody>
                {menuEdits.map((entry, idx) => entry.deleted ? null : (
                  <tr key={entry.id ?? `new-${idx}`} className="am-entity-tr">
                    <td className="am-entity-td">
                      <input
                        className="am-entity-input"
                        value={entry.name}
                        onChange={(e) => setMenuEdits((prev) => prev.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))}
                      />
                    </td>
                    <td className="am-entity-td-x">
                      <button
                        className="am-entity-x"
                        type="button"
                        onClick={() => setMenuEdits((prev) => entry.isNew ? prev.filter((_, i) => i !== idx) : prev.map((r, i) => i === idx ? { ...r, deleted: true } : r))}
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="am-entity-tr am-entity-tr-new">
                  <td colSpan={2} className="am-entity-td">
                    <button
                      className="am-entity-add-btn"
                      type="button"
                      onClick={() => setMenuEdits((prev) => [...prev, { id: null, name: "", originalName: "", isNew: true, deleted: false }])}
                    >
                      + Add menu
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="am-modal-footer">
              <button className="am-btn" type="button" onClick={() => setMenusModalOpen(false)}>Cancel</button>
              <button className="am-btnPrimary" type="button" onClick={saveMenus} disabled={saving}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* ---- Categories Modal ---- */}
      {catsModalOpen && (
        <div className="am-modal-backdrop" onClick={() => setCatsModalOpen(false)}>
          <div className="am-modal" onClick={(e) => e.stopPropagation()}>
            <div className="am-modal-header">
              <h2 className="am-cardTitle">Edit Categories</h2>
              <button className="am-modal-close" type="button" onClick={() => setCatsModalOpen(false)}>
                &times;
              </button>
            </div>

            <div className="am-field" style={{ marginBottom: 16 }}>
              <label className="am-label" htmlFor="cat-modal-menu">Menu</label>
              <select
                id="cat-modal-menu"
                className="am-select"
                value={catMenuFilter}
                onChange={(e) => updateCatEditsForMenu(e.target.value)}
              >
                {menus.map((m) => (
                  <option key={m.id} value={String(m.id)}>{m.name}</option>
                ))}
              </select>
            </div>

            <table className="am-entity-table">
              <tbody>
                {catEdits.map((entry, idx) => entry.deleted ? null : (
                  <tr key={entry.id ?? `new-${idx}`} className="am-entity-tr">
                    <td className="am-entity-td">
                      <input
                        className="am-entity-input"
                        value={entry.name}
                        onChange={(e) => setCatEdits((prev) => prev.map((r, i) => i === idx ? { ...r, name: e.target.value } : r))}
                      />
                    </td>
                    <td className="am-entity-td-x">
                      <button
                        className="am-entity-x"
                        type="button"
                        onClick={() => setCatEdits((prev) => entry.isNew ? prev.filter((_, i) => i !== idx) : prev.map((r, i) => i === idx ? { ...r, deleted: true } : r))}
                      >
                        &times;
                      </button>
                    </td>
                  </tr>
                ))}
                <tr className="am-entity-tr am-entity-tr-new">
                  <td colSpan={2} className="am-entity-td">
                    <button
                      className="am-entity-add-btn"
                      type="button"
                      onClick={() => setCatEdits((prev) => [...prev, { id: null, name: "", originalName: "", isNew: true, deleted: false }])}
                    >
                      + Add category
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="am-modal-footer">
              <button className="am-btn" type="button" onClick={() => setCatsModalOpen(false)}>Cancel</button>
              <button className="am-btnPrimary" type="button" onClick={saveCategories} disabled={saving}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
